import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import SenseMeaning from './SenseMeaning';
import SenseCount from '../../annotations/SenseCount';
import './WordSelector.css';

/**
 * WordSelector — renders subtitle words as tappable tokens.
 * Tapping a word toggles it in/out of the selection.
 * Shows existing annotations for the selection via "Show Annotations" button.
 *
 * Props:
 *   subtitle — the currently selected subtitle object { id, originalText, videoId, ... }
 *   onSelectionReady(selectedText, wordIndices) — called when user has a selection ready to annotate
 *   onExistingAnnotation(annotation) — called when an existing annotation is found
 */
function WordSelector({ subtitle, onSelectionReady, onExistingAnnotation }) {
  const [selectedIndices, setSelectedIndices] = useState([]);
  const [existingAnnotations, setExistingAnnotations] = useState([]);
  const [checking, setChecking] = useState(false);

  // Reset selection when subtitle changes
  useEffect(() => {
    setSelectedIndices([]);
    setExistingAnnotations([]);
  }, [subtitle?.id]);

  if (!subtitle) return null;

  // English-only subtitles have no French words to tap.
  if (!subtitle.originalText) {
    return (
      <p className="word-selector-label">
        This subtitle is English only. Annotate its French words under Annotations below.
      </p>
    );
  }

  const words = subtitle.originalText.split(/\s+/);

  const toggleWord = (index) => {
    setSelectedIndices((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index);
      }
      return [...prev, index].sort((a, b) => a - b);
    });
    // Clear previous lookup results when selection changes
    setExistingAnnotations([]);
  };

  const selectedText = selectedIndices.map((i) => words[i]).join(' ');

  const handleShowAnnotations = async () => {
    if (!selectedText || !subtitle.videoId) return;
    setChecking(true);
    setExistingAnnotations([]);

    try {
      // Look up existing annotations for this subtitle with matching surface text
      const q = query(
        collection(db, 'annotations'),
        where('videoId', '==', subtitle.videoId),
        where('subtitleId', '==', subtitle.id),
        where('surfaceText', '==', selectedText)
      );
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const annotations = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setExistingAnnotations(annotations);
        onExistingAnnotation && onExistingAnnotation(annotations[0]);
      } else {
        setExistingAnnotations([]);
      }
    } catch (e) {
      console.error('Error checking annotations:', e);
    }
    setChecking(false);
  };

  const handleAnnotate = () => {
    onSelectionReady && onSelectionReady(selectedText, selectedIndices);
  };

  return (
    <div className="word-selector">
      <div className="word-selector-label">Tap words to select:</div>

      <div className="word-selector-tokens">
        {words.map((word, index) => (
          <button
            key={index}
            className={`word-token ${selectedIndices.includes(index) ? 'word-token--selected' : ''}`}
            onClick={() => toggleWord(index)}
            aria-pressed={selectedIndices.includes(index)}
            aria-label={`${word}${selectedIndices.includes(index) ? ' (selected)' : ''}`}
          >
            {word}
          </button>
        ))}
      </div>

      {/* Selection text box */}
      {selectedIndices.length > 0 && (
        <div className="word-selector-selection">
          <span className="word-selector-selection-text">{selectedText}</span>
        </div>
      )}

      {/* Action buttons */}
      {selectedIndices.length > 0 && (
        <div className="word-selector-actions">
          <button
            className="word-selector-show-btn"
            onClick={handleShowAnnotations}
            disabled={checking}
          >
            {checking ? 'Checking…' : 'Show Annotations'}
          </button>
          <button
            className="word-selector-annotate-btn"
            onClick={handleAnnotate}
          >
            Annotate
          </button>
        </div>
      )}

      {/* Existing annotation result */}
      {existingAnnotations.length > 0 && (
        <div className="word-selector-existing">
          <p className="word-selector-existing-label">Existing annotation found:</p>
          {existingAnnotations.map((ann) => (
            <div key={ann.id} className="word-selector-existing-item">
              <span className="word-selector-existing-surface">"{ann.surfaceText}"</span>
              <span className="word-selector-existing-arrow">→</span>
              <span className="word-selector-existing-entry">
                {ann.senseId ? <SenseMeaning senseId={ann.senseId} /> : 'an entry in the old hoja dictionary'}
              </span>
              {ann.senseId && <SenseCount senseId={ann.senseId} />}
            </div>
          ))}
        </div>
      )}

      {/* No annotation found */}
      {existingAnnotations.length === 0 && checking === false && selectedIndices.length > 0 && (
        <div className="word-selector-no-result" />
      )}
    </div>
  );
}

export default WordSelector;
